// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.367Z
// Collection: paymentDeposits/$depositId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Generic top-ups from user wallet to their balance PDA. Onchain. The payer transfers funds (SOL/USDC/USDT) from their connected wallet into their reusable userPaymentAccounts PDA. Not tied to any session — these are reusable balances. Records are publicly auditable.
 */

export interface PaymentDepositsRequest {
  payer: AddressType;
  amt: number | TimeOperation | IncrementOperation | TokenAmount;
  rail: string;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
  userAccountId: string;
}

export interface PaymentDepositsResponse {
  payer: string;
  amt: number;
  rail: string;
  ts: number;
  userAccountId: string;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a PaymentDeposits operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPaymentDeposits(depositId: string, data?: PaymentDepositsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `paymentDeposits/${depositId}`, document: doc };
}

/**
 * Connected-wallet top-up. The create rule enforces (1) @newData.payer == @user.address (payer signs their own deposit) and (2) the target userPaymentAccounts/$userAccountId belongs to @user.address (so a user cannot deposit into someone else's PDA). The onchain hook is idempotent: it ensures the PDA exists via @AccountPlugin.createAccount(@newData.userAccountId) and then transfers @newData.amt from @user.address to the PDA on the rail-appropriate mint (SOL via @constants.SOL, USDC via @constants.USDC_MINT, USDT via @constants.USDT_MINT). The frontend MUST pass the deterministic userAccountId (hash of the wallet) as @newData.userAccountId so the rule and hook can address the correct PDA. Deposits are session-agnostic — attribution to a specific checkout session happens later via balanceApplicationRequests + balanceSettlementActions. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPaymentDeposits(depositId: string, data?: PaymentDepositsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`paymentDeposits/${depositId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting PaymentDeposits: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
   (Get Single Item)
 */
export async function getPaymentDeposits(depositId: string): Promise<PaymentDepositsResponse | null> {
  try {
    const result = await get(`paymentDeposits/${depositId}`);
    return result as PaymentDepositsResponse | null;
  } catch (error) {
    console.error(`Error getting PaymentDeposits: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single PaymentDeposits document. (
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
  )
 */
export function subscribePaymentDeposits(callback: (data: PaymentDepositsResponse | null) => void, depositId: string): Promise<() => Promise<void>> {
  return subscribe(`paymentDeposits/${depositId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PaymentDepositsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to PaymentDeposits at path '${`paymentDeposits/${depositId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many PaymentDeposits items from collection paymentDeposits
 
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
  
 */
export async function getManyPaymentDeposits(filter: string = ""): Promise<PaymentDepositsResponse[]> {
  try {
    const results = await get("paymentDeposits", { prompt: filter });
    return Array.isArray(results) ? results as PaymentDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting PaymentDeposits collection at path 'paymentDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PaymentDeposits collection at paymentDeposits
 
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
  
 */
export function subscribeManyPaymentDeposits(callback: (data: PaymentDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("paymentDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PaymentDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PaymentDeposits collection at path '${"paymentDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all PaymentDeposits items from collection paymentDeposits
 
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
  
 */
export async function getAllPaymentDeposits(filter: string = ""): Promise<PaymentDepositsResponse[]> {
  try {
    const results = await get("paymentDeposits", { prompt: filter });
    return Array.isArray(results) ? results as PaymentDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting PaymentDeposits collection at path 'paymentDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PaymentDeposits collection at paymentDeposits
 
  Read Operation Details: Public read. Deposit records are publicly auditable — they only contain the payer wallet, target userAccountId, amount, rail, and timestamp. The mint is derived from the trusted rail field, not supplied by the client.
  
 */
export function subscribeAllPaymentDeposits(callback: (data: PaymentDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("paymentDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PaymentDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PaymentDeposits collection at path '${"paymentDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count PaymentDeposits items in collection paymentDeposits.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPaymentDeposits(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("paymentDeposits", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting PaymentDeposits at path 'paymentDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on PaymentDeposits items in collection paymentDeposits.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePaymentDeposits(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("paymentDeposits", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating PaymentDeposits at path 'paymentDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

